<script setup>
import { ref, onMounted, defineExpose } from "vue";

const props = defineProps({
  article: { type: Object, required: true }
});

// Emit close → ModalManager
const emit = defineEmits(["close"]);

// ============================================================
// STATE
// ============================================================
const loading = ref(true);
const error = ref(null);

const groups = ref([]);
const variants = ref([]);

const editorRoot = ref(null);

// Expose to ModalManager for detectValidation()
defineExpose({
  variants
});

// ============================================================
// LOAD MATRIX
// ============================================================
async function loadMatrix() {
  loading.value = true;
  error.value = null;

  try {
    const apiKey = localStorage.getItem("api_key");
    const res = await fetch(`/api/variants/${props.article.id}/variants-tree-full`, {
      headers: {
        "Content-Type": "application/json",
        ...(apiKey ? { "x-api-key": apiKey } : {})
      }
    });

    const data = await res.json();
    groups.value = data.groups || [];
    variants.value = data.variants || [];

  } catch (err) {
    console.error("❌ variants-tree-full failed:", err);
    error.value = "Failed to load variant matrix.";
  }
  loading.value = false;

}

onMounted(loadMatrix);

// ============================================================
// HELPERS
// ============================================================
function parsePairs(pnordered) {
  return pnordered.split("__").map(chunk => {
    const [group_id, type_id] = chunk.split("_").map(Number);
    return { group_id, type_id };
  });
}

function findTypeLabel(variant, groupId) {
  const entry = variant.pnordered
    .split("__")
    .map(c => c.split("_").map(Number))
    .find(([gid]) => gid === groupId);

  const typeId = entry ? entry[1] : null;
  const typeObj = variant.types.find(t => t.type_id === typeId);

  return typeObj ? typeObj.type_label : "-";
}

// ============================================================
// EDITOR-SIDE VALIDATION LOGIC
// ============================================================
/**
 * Returns TRUE if at least one row contains:
 *  - stock > 0
 *  - OR SKU is non-empty
 *  - OR price adjustment ≠ 0
 */
function hasValidStock() {
  return variants.value.some(v =>
    Number(v.curstock) > 0 ||
    (v.sku_thirdparty || "").trim() !== "" ||
    Number(v.upprice) !== 0
  );
}

// ============================================================
// SAVE
// ============================================================
async function save() {
  try {
    const list = variants.value.map(v => ({
      pnordered: v.pnordered,
      curstock: Number(v.curstock) || 0,
      sku_thirdparty: (v.sku_thirdparty || "").trim(),
      upprice: Number(v.upprice) || 0,
      pairs: parsePairs(v.pnordered)
    }));

    await fetch(`/api/articles/${props.article.id}/stocktypes`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": localStorage.getItem("api_key") || ""
      },
      body: JSON.stringify({ list })
    });

    // Pass TRUE/FALSE depending on values changed
    emit("close", hasValidStock());

  } catch (err) {
    console.error("❌ saveMatrixStocktypes error:", err);
    error.value = "Saving failed.";
  }
}

// ============================================================
// CANCEL
// ============================================================
function cancel() {
  emit("close", hasValidStock());
}
</script>


<template>
  <div class="st-editor" ref="editorRoot">

    <div v-if="loading" class="nz-loading">Loading variant matrix…</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="groups.length === 0" class="nz-no-records">No <span class="accent">price neutral/non display</span> types chosen for <i>{{ props.article.label }}</i> - id {{ props.article.id }}</div>
    <div v-else>
      <table class="st-matrix">
        <thead>
          <tr>
            <th v-for="g in groups" :key="g.group_id">{{ g.label }}</th>
            <th>Stock</th>
            <th>SKU</th>
            <th>Up-Price</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="v in variants" :key="v.pnordered">

            <!-- Dynamic labels -->
            <td v-for="g in groups" :key="g.group_id">
              {{ findTypeLabel(v, g.group_id) }}
            </td>

            <!-- Stock -->
            <td>
              <input 
                type="number"
                class="st-mx-stock"
                v-model.number="v.curstock"
                :data-nz-valid="Number(v.curstock) > 0"
              />
            </td>

            <!-- SKU -->
            <td>
              <input 
                type="text"
                class="st-mx-sku"
                v-model="v.sku_thirdparty"
                :data-nz-valid="(v.sku_thirdparty || '').trim() !== ''"
              />
            </td>

            <!-- Up-Price Price Adjust -->
            <td>
              <input 
                type="number"
                step="0.01"
                class="st-mx-adj"
                v-model.number="v.upprice"
                :data-nz-valid="Number(v.upprice) !== 0"
              />
            </td>

          </tr>
        </tbody>
      </table>
    </div>

    <div class="nz-modal-footer">
      <button class="btn btn-primary" @click="save">Save</button>
      <button class="btn btn-warning" @click="cancel">Cancel</button>
    </div>

  </div>
</template>


<style>
.error-msg {
  padding: 20px;
  color: red;
}

.st-matrix {
  width: 100%;
  border-collapse: collapse;
}

.st-matrix th,
.st-matrix td {
  border: 1px solid #ddd;
  padding: 6px 10px;
}

.st-matrix input {
  width: 100%;
  padding: 4px;
}
</style>
